import { useCallback, useEffect, useState } from "react";
import useAuth from "./useAuth";
import useWorkMode from "./useWorkMode";
import { dashboardService } from "../services/dashboard.service";
import { onDashboardRefresh } from "../utils/dashboardEvents";

const EMPTY = {
  companyTotalRecords: 0,
  companyOwnRecords: 0,
  allBanksNbfcsRecords: 0,
};

export default function useLinkedUploadAgencies({ enabled = true } = {}) {
  const { auth } = useAuth();
  const { isOffline } = useWorkMode();
  const [summary, setSummary] = useState(EMPTY);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!enabled || !auth?.token || isOffline || navigator.onLine === false) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await dashboardService.getLinkedUploadAgencies();
      const data = res?.data || {};
      setSummary({
        companyTotalRecords: Number(data.companyTotalRecords) || 0,
        companyOwnRecords: Number(data.companyOwnRecords) || 0,
        allBanksNbfcsRecords: Number(data.allBanksNbfcsRecords) || 0,
      });
    } catch (err) {
      setSummary(EMPTY);
      setError(
        err?.response?.data?.message ||
          "Unable to load agency upload records."
      );
    } finally {
      setLoading(false);
    }
  }, [auth?.token, enabled, isOffline]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => onDashboardRefresh(load), [load]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load]);

  return { ...summary, loading, error, reload: load };
}
